/* eslint-disable react/prop-types */
import * as React from "react";
import jspdf from "jspdf";
import autoTable from "jspdf-autotable";
import { Typography } from "@mui/material";

type exportButtonDefaultProps = {
  data: string[][];
  pickedStates: any;
  style?: {};
  isDisabled?: boolean;
  color?: string;
  variant?: string;
  onClick?: () => void;
};

function determineColumnWidths(header: string | any[], body: string | any[]) {
  /*
    This function looks at one table at a time. It adds up the lengths of all text in a column and
    stores that as subtotals, and adds up all of those lengths as a total. From here, the ratio of
    each column's subtotal length to total length is understood to be the ratio of width the column
    "deserves". Multiplying this ratio by the desiredWidth gives a literal numerical width for each
    column. Some of these values are too low, however, and do not display well. Therefore, any value
    under the minimumWidth will be set to the minimumWidth. This increases totalWidth to be above the
    desiredWidth, so the extra width will be slowly taken away from the widest columns until the
    totalWidth is at the desiredWidth. 
    */
  const MINIMUM_PDF_COLUMN_WIDTH = 13; //the minimum width a column is allowed to be (not guaranteed for large amounts of columns)
  const DESIRED_PDF_TABLE_WIDTH = 180; //the desired total width of all columns together. 180 is a good value for portrait-oriented pdf pages.
  let numberOfColumns = header.length;
  let numberOfRows = body.length;
  let columnWidths: number[] = []; //the final desired widths for each column
  let columnSubTotals = []; //numerator of each ratio
  let totalLength = 0; //denominator for each ratio

  //determine subtotal and total length(s)
  for (let col = 0; col < numberOfColumns; col++) {
    let subTotalLength = 0;
    for (let row = 0; row < numberOfRows; row++) {
      subTotalLength += body[row][col].length;
    }
    columnSubTotals.push(subTotalLength);
    totalLength += subTotalLength;
  }

  //increase any widths that are too small, then add up the totalWidth
  let totalWidth = 0;
  columnSubTotals.forEach((subTotal) => {
    let width = Math.floor((subTotal / totalLength) * DESIRED_PDF_TABLE_WIDTH);
    if (width < MINIMUM_PDF_COLUMN_WIDTH) {
      width = MINIMUM_PDF_COLUMN_WIDTH;
    }
    totalWidth += width;
    columnWidths.push(width);
  });

  //take away width from the largest column until total is within desired
  while (totalWidth > DESIRED_PDF_TABLE_WIDTH) {
    let indexOfWidest = columnWidths.reduce(
      (iMax, current, currentIndex, array) =>
        current > array[iMax] ? currentIndex : iMax,
      0,
    );
    columnWidths[indexOfWidest] -= 1;
    totalWidth--;
  }

  // transform widths array into dictionary to be passed to autotable
  let columnWidthsDictionary = {} as any;
  columnWidths.forEach((width, index) => {
    let item = { cellWidth: width };
    columnWidthsDictionary[index] = item;
  });
  return columnWidthsDictionary;
}

// Function wrapper for print handler.
function ExportButton({ data, onClick }: exportButtonDefaultProps) {
  //Handles onClick event
  const handlePrint = () => {
    const doc = new jspdf();
    const today = new Date();
    const dateString = today.toLocaleDateString();
    doc.text(`Opus Export - ${dateString}`, 14, 20);
    // Add disclaimer at the top of the PDF
    doc.setFontSize(10);
    doc.setTextColor(150);
    doc.text(
      "DISCLAIMER: This material is intended as a guide to various state requirements. Please refer to the referenced statutes for additional information and contact the legal department with further questions, or if you need clarification.",
      14,
      30,
      { maxWidth: 180 },
    );
    doc.setFontSize(12);
    doc.setTextColor(0);
    let headers: any[] = [];
    let body: any[] = [];
    data.map((topic) => {
      if (topic[0]["length"] > 2) {
        headers.push(topic);
      } else {
        body.push(topic);
      }
    });
    let counter = 0;
    let numberOfTables = headers.length;
    let numberOfRows = body.length;
    for (let i = 0; i < numberOfTables; i++) {
      let separateBody = [];
      for (let q = 0; q < numberOfRows / numberOfTables; q++) {
        separateBody.push(body[counter]);
        counter++;
      }
      let widths = determineColumnWidths(headers[i], separateBody);
      autoTable(doc, {
        columns: headers[i],
        body: separateBody,
        columnStyles: widths,
        startY: 45, // ensure tables start below the disclaimer
      });
    }
    doc.save("table.pdf");
  };

  return (
    <div>
      <Typography onClick={handlePrint}>PDF</Typography>
    </div>
  );
}

export default ExportButton;
